import { BreakpointTrackerContext } from '@jutro/layout';
import { useHistory } from 'react-router-dom';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { Loader, useModal } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { UserService } from 'gw-capability-gateway';
import TWIABatchService from "ent-capability-policyportfolio/services/TWIABatchService.js";
import moment from 'moment';
import _TWIADateUtil from "../../../../modules/twia-portals-viewmodel-js/aspects/_TWIADateUtil";
import styles from './AddTransferRequest.module.scss';
import messages from '../TWIABatchesLanding/TWIABatchesLanding.messages';
import metadata from './AddTransferRequest.metadata.json5';

function AddTransferRequest() {
    const breakpoint = useContext(BreakpointTrackerContext);
    const {
        isComponentValid,
        onValidate,
    } = useValidation('AddTransferRequest');
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const [isLoading, setIsLoading] = useState(false);
    const { showAlert, showConfirm } = useModal();
    const [showErrors, setShowErrors] = useState(false);
    const [availableProducerCode, setAvailableProducerCode] = useState([]);
    const [newBatchVM, updateNewBatchVM] = useState({});
    const [processDate, setProcessDate] = useState();
    const [effectiveDate, setEffectiveDate] = useState();
    const [prevProducerCode, setPrevProducerCode] = useState([]);
    const [newProducerCode, setNewProducerCode] = useState([]);
    const history = useHistory();
    const [effDateMismatchValidationMessage, setEffDateMismatchValidationMessage] = useState([]);
    const [proDateMismatchValidationMessage, setProDateMismatchValidationMessage] = useState([]);

    useEffect(() => {
        if (onValidate && !_.isNull(isComponentValid)) {
            onValidate(isComponentValid, 'AddTransferRequest');
        }
    }, [isComponentValid, onValidate]);

    const fillProducerCodes = useCallback(async () => {
        const availableProducerCodes = [];

        await UserService.getAvailableProducerCodesForCurrentUser(authHeader)
            .then(codes => {
                if (codes) {
                    codes.forEach((code) => availableProducerCodes.push(code));
                }
            });

        const res = availableProducerCodes.map(co => {
            const availableProducerCodesforUser = {
                code: co.code,
                name: co.displayValue,
                ...co
            };

            return availableProducerCodesforUser;
        });

        setAvailableProducerCode(res);

        return availableProducerCodes;
    }, [authHeader]);

    useEffect(() => {

        const batchView = viewModelService.create({}, 'pc', 'edge.capabilities.gateway.bulkproducerchange.dto.TWIABPCBatch_ExtDTO');

        updateNewBatchVM(batchView);
        fillProducerCodes();
    }, [fillProducerCodes, viewModelService]);

    useEffect(() => {
        const date = new Date(_TWIADateUtil.beginOfToday());

        _.set(newBatchVM, 'targetDate', date);
        updateNewBatchVM(newBatchVM);
    }, [newBatchVM]);

    const writeValue = useCallback((value, path) => {
        if (path === 'targetDate') {
            if (_.isNil(value) || value === '') { // check for both null and empty string
                _.set(newBatchVM, path, undefined);
                setProDateMismatchValidationMessage([translator(messages.dateMustmatch)]);
                setProcessDate();
            } else {
                setProDateMismatchValidationMessage([]);

                const targetDate = moment(value);

                setProcessDate(value);
                _.set(newBatchVM, path, targetDate);
            }

            updateNewBatchVM(newBatchVM);
        }
    
        if (path === 'effectiveDate_Ext') {
            if (_.isNil(value) || value === '') { // check for both null and empty string
                _.set(newBatchVM, path, undefined);
                setEffDateMismatchValidationMessage([translator(messages.dateMustmatch)]);
                setEffectiveDate();
            } else {
                setEffDateMismatchValidationMessage([]);

                const effDate = moment(value);

                setEffectiveDate(value);
                _.set(newBatchVM, path, effDate);
            }

            updateNewBatchVM(newBatchVM);
        }
    
        if (path === 'oldProducerCode') {
            setPrevProducerCode(value);
            _.set(newBatchVM, path, value);
            updateNewBatchVM(newBatchVM);
        }
    
        if (path === 'newProducerCode') {
            setNewProducerCode(value);
            _.set(newBatchVM, path, value);
            updateNewBatchVM(newBatchVM);
        }
    }, [newBatchVM, translator]);
    

    const createBatch = async () => {

        if (!isComponentValid) {
            setShowErrors(true);

            if (_.isUndefined(_.get(newBatchVM, 'targetDate'))) {
                setProDateMismatchValidationMessage([translator(messages.dateMustmatch)]);
            } else {
                setProDateMismatchValidationMessage([]);
            }

            if (_.isUndefined(_.get(newBatchVM, 'effectiveDate_Ext'))) {
                setEffDateMismatchValidationMessage([translator(messages.dateMustmatch)]);
            } else {
                setEffDateMismatchValidationMessage([]);
            }

            return false;
        }

        const targetDate = moment(_.get(newBatchVM, 'targetDate'));
        const effDate = moment(_.get(newBatchVM, 'effectiveDate_Ext'));
        const todayYear = new Date(_TWIADateUtil.beginOfToday()).getFullYear();
        const currentDate = moment();

        _.set(newBatchVM, 'effectiveDate_Ext', effDate);
        updateNewBatchVM(newBatchVM);

        if (targetDate.isBefore(currentDate, 'day')) {
            showAlert({
                title: translator(messages.newBatchRequest),
                message: translator(messages.newBatchProcessDateWarning),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: translator(messages.close)
            });

            return;
        }

        if (effDate.year() < todayYear) {
            showAlert({
                title: translator(messages.transferRequestDetail),
                message: translator(messages.newBatchEffectiveDateWarning),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: translator(messages.close)
            });

            return;
        }

        if (_.get(newBatchVM, 'oldProducerCode') === _.get(newBatchVM, 'newProducerCode')) {
            showAlert({
                title: translator(messages.newBatchRequest),
                message: translator(messages.newAndOldSameCode),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: translator(messages.close)
            });

            return;
        }

        try {
            await TWIABatchService.createOrUpdateBatch(newBatchVM, authHeader);

            showAlert({
                title: translator(messages.newBatchRequest),
                message: translator(messages.transferRequestMsg),
                status: 'warning',
                icon: 'gw-help-outline',
                confirmButtonText: translator(messages.ok)
            })
                .then((result) => {
                    if (result === 'confirm') {
                        history.push(`/batches`);
                    }
                })
        } catch (err) {

            const errorMessage = _.split(err.baseError, 'ErrorMessage: ')[2]?.trim();

            if (errorMessage) {
                showAlert({
                    title: 'Error',
                    message: errorMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: translator(messages.close)
                })
            } else {
                showAlert({
                    title: 'Error',
                    message: translator(messages.unableToCancel),
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: translator(messages.close)
                })
            }
        }
    };

    const onCancel = async () => {
        const cancelDialogPromise = {
            title: translator(messages.newBatchCancelHeading),
            message: translator(messages.newBatchCancelMsg),
            status: 'warning',
            icon: 'gw-help-outline',
            confirmButtonText: translator(messages.yes),
            cancelButtonText: translator(messages.no)
        };

        const result = await showConfirm(cancelDialogPromise);

        if (result === 'confirm') {
            history.push(`/batches`);
        }
    };

    const overrideProps = {
        '@field': {
            // apply to all fields
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        processDate: {
            label: translator(messages.processDate),
            showRequired: true,
            value: processDate || _.get(newBatchVM, 'targetDate'),
            defaultValue: _.get(newBatchVM, 'targetDate'),
            validationMessages: proDateMismatchValidationMessage
        },
        effectiveDate: {
            label: translator(messages.effectiveDate),
            showRequired: true,
            value: effectiveDate || _.get(newBatchVM, 'effectiveDate_Ext'),
            validationMessages: effDateMismatchValidationMessage
        },
        prevProducerCode: {
            availableValues: availableProducerCode,
            showRequired: true,
            value: prevProducerCode
        },
        newProducerCode: {
            availableValues: availableProducerCode,
            showRequired: true,
            value: newProducerCode
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            createBatch,
            onCancel
        },

    }

    return (
        <Loader loaded={!isLoading}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={newBatchVM}
                overrideProps={overrideProps}
                onModelChange={updateNewBatchVM}
                onValueChange={writeValue}
                showErrors={showErrors}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                onValidationChange={onValidate}
            />
        </Loader>
    );
}

export default AddTransferRequest;